import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import { useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { useParams, useSearchParams } from 'react-router-dom';
import QuitForm from '@/components/pages/create-webinar/live/quit/QuitForm';
import VideosForm from '@/components/pages/create-webinar/live/VideosForm';
import PdfsForm from '@/components/pages/create-webinar/live/pdf/PdfsForm';
import { Separator } from '@/components/ui/separator';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OffersForm from '@/components/pages/create-webinar/live/offer/OffersForm';
import SharedResourcesForm from '@/components/pages/create-webinar/live/resources/SharedResourcesForm';

interface LiveSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}
export default function LiveSection({ currentWebinarId, isEdit, onSaveSuccess }: LiveSectionProps) {
  const { t } = useTranslation('createWebinar');
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarIdString = webinarIdParam || currentWebinarId;
  const { currentWorkspace } = useAppSelector((state: RootState) => state.workspace);
  const isYouTube = useAppSelector(
    (state: RootState) => state.webinar.currentWebinar?.transmissionMode === 'YOUTUBE',
  );
  const webinarId =
    typeof webinarIdString === 'number' ? webinarIdString : parseInt(webinarIdString ?? '0', 10);

  const [openPdfAccordion, setOpenPdfAccordion] = useState(false);
  const [searchParams] = useSearchParams();
  // Lien profond depuis la pop-up de recommandations : `?focus=offers` ouvre le bloc Offres.
  // Le paramètre est retiré par l'onglet parent, pas ici : la section se remonte au fil des
  // chargements et doit pouvoir rouvrir le bloc à chaque fois.
  const [openOffersAccordion, setOpenOffersAccordion] = useState(
    () => searchParams.get('focus') === 'offers',
  );

  // Même raison que pour l'onglet : le lien profond peut arriver alors que la section est
  // déjà montée, auquel cas l'initialisation ci-dessus n'est jamais rejouée.
  useEffect(() => {
    if (searchParams.get('focus') !== 'offers') return;
    setOpenOffersAccordion(true);
  }, [searchParams]);

  if (!webinarId || !currentWorkspace) {
    return <div>{t('liveSection.fetchError')}</div>;
  }

  return (
    <TitleLayout
      title={t('liveSection.title')}
      subtitle={t('liveSection.subtitle')}
      isEdit={isEdit}
      description={t('liveSection.description')}
    >
      {!isYouTube && (
        <>
          <PdfsForm
            webinarId={webinarId}
            workspaceId={currentWorkspace.id}
            isEdit={isEdit}
            openAccordionTrigger={openPdfAccordion}
            onAccordionOpened={() => setOpenPdfAccordion(false)}
          />
          <Separator className="bg-card" />
          <VideosForm webinarId={webinarId} workspaceId={currentWorkspace.id} isEdit={isEdit} />
          <Separator className="bg-card" />
        </>
      )}
      <SharedResourcesForm
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
      />
      <Separator className="bg-card" />
      <OffersForm
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
        openAccordionTrigger={openOffersAccordion}
        onAccordionOpened={() => setOpenOffersAccordion(false)}
      />
      <Separator className="bg-card" />
      <QuitForm
        webinarId={webinarId}
        workspaceId={currentWorkspace.id}
        isEdit={isEdit}
        onSaveSuccess={onSaveSuccess}
      />
    </TitleLayout>
  );
}
